import {
  CF_ALL_ACCESS_MONTHLY_PRICE_USD,
  CF_ALL_ACCESS_MONTHLY_PRICE_OTHER,
  CF_ALL_ACCESS_YEARLY_PRICE,
  CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
  CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE,
  CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE,
  CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE,
  VAT_INCLUDED_CF_ALL_ACCESS_MONTHLY_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE_NL,
  VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE_NL,
  CF_COINS_TOP_UP_25K_PRICE_USD,
  CF_COINS_TOP_UP_100K_PRICE_USD,
  CF_COINS_TOP_UP_250K_PRICE_USD,
  CF_COINS_TOP_UP_1000K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_25K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_100K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_250K_PRICE_USD,
  VAT_INCLUDED_CF_COINS_TOP_UP_1000K_PRICE_USD,
  getStudioAiPlanPrices,
  getStudioAiPlanCoins,
} from 'constants/prices'
import type { CountryCode } from 'codegen/types'
import { CurrencyCode } from 'codegen/types'
import type { SubscriptionPlan } from '@repo/graphql-types'
import * as React from 'react'
import { useCurrentCurrency } from './use-current-currency'
import type { CurrentCurrency } from './use-current-currency'

import { useUserLocation } from '@repo/hooks-use-user-location'

const ForcedCountryContext = React.createContext<CountryCode | undefined>(undefined)

export function ForcedCountryProvider({
  country,
  children,
}: {
  country: CountryCode | undefined
  children: React.ReactNode
}) {
  return React.createElement(ForcedCountryContext.Provider, { value: country }, children)
}

const shouldPricesIncludeVat = (country: CountryCode | undefined) => {
  return country === 'NL'
}

interface Prices {
  monthly: number
  yearly: number
  yearlyPromo: number
  yearlyMonthlySplit: number
  yearlyPromoMonthlySplit: number
  yearlyPromoMonthlyFull: number
  studioMonthly: number
  studioYearly: number
  coinsTopUp25k: number
  coinsTopUp100k: number
  coinsTopUp250k: number
  coinsTopUp1000k: number
}

function decidePrices(
  country: CountryCode | undefined,
  currency: CurrentCurrency,
  studioPlan?: SubscriptionPlan,
) {
  const includeVat = shouldPricesIncludeVat(country)
  const studio = getStudioAiPlanPrices(studioPlan, { includeVat })

  const prices: Prices = includeVat
    ? {
        monthly: VAT_INCLUDED_CF_ALL_ACCESS_MONTHLY_PRICE_NL,
        yearly: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PRICE_NL,
        yearlyPromo: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_PRICE_NL,
        yearlyMonthlySplit: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE_NL,
        yearlyPromoMonthlySplit: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE_NL,
        yearlyPromoMonthlyFull: VAT_INCLUDED_CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE_NL,
        studioYearly: studio.yearly,
        studioMonthly: studio.monthly,
        coinsTopUp25k: VAT_INCLUDED_CF_COINS_TOP_UP_25K_PRICE_USD,
        coinsTopUp100k: VAT_INCLUDED_CF_COINS_TOP_UP_100K_PRICE_USD,
        coinsTopUp250k: VAT_INCLUDED_CF_COINS_TOP_UP_250K_PRICE_USD,
        coinsTopUp1000k: VAT_INCLUDED_CF_COINS_TOP_UP_1000K_PRICE_USD,
      }
    : {
        monthly:
          currency.code === CurrencyCode.Usd
            ? CF_ALL_ACCESS_MONTHLY_PRICE_USD
            : CF_ALL_ACCESS_MONTHLY_PRICE_OTHER,
        yearly: CF_ALL_ACCESS_YEARLY_PRICE,
        yearlyPromo: CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
        yearlyMonthlySplit: CF_ALL_ACCESS_YEARLY_MONTHLY_SPLIT_PRICE,
        yearlyPromoMonthlySplit: CF_ALL_ACCESS_YEARLY_PROMO_MONTHLY_SPLIT_PRICE,
        yearlyPromoMonthlyFull: CF_ALL_ACCESS_YEARLY_MONTHLY_FULL_PRICE,
        studioYearly: studio.yearly,
        studioMonthly: studio.monthly,
        coinsTopUp25k: CF_COINS_TOP_UP_25K_PRICE_USD,
        coinsTopUp100k: CF_COINS_TOP_UP_100K_PRICE_USD,
        coinsTopUp250k: CF_COINS_TOP_UP_250K_PRICE_USD,
        coinsTopUp1000k: CF_COINS_TOP_UP_1000K_PRICE_USD,
      }

  return { currency, prices }
}

type LocationCountry = ReturnType<typeof useUserLocation>['country']

const getPricingCountry = (country: LocationCountry): CountryCode | undefined => {
  if (country === 'XX' || country === 'T1') {
    return undefined
  }

  return country
}

/**
 * All prices for the current user, with correct currency and VAT.
 *
 * `studioPlan` only affects `studioYearly` / `studioMonthly`: pass the subscriber's own plan (from
 * `subscription.type`) to show their price; omit it to get the standard plan.
 */
export const usePricesForUser = (forceCountry?: CountryCode, studioPlan?: SubscriptionPlan) => {
  const { country } = useUserLocation()
  const contextCountry = React.useContext(ForcedCountryContext)
  const defaultCurrency = useCurrentCurrency()

  const effectiveCountry = forceCountry ?? contextCountry ?? getPricingCountry(country)
  const prices = decidePrices(effectiveCountry, defaultCurrency, studioPlan)

  return {
    ...prices,
    country,
    pricesIncludeVat: shouldPricesIncludeVat(effectiveCountry),
    studioCoins: getStudioAiPlanCoins(studioPlan),
  }
}
